import { h, Fragment } from "preact";
import { useEffect, useMemo } from "preact/hooks";
import type { MemberEmailElement } from "../../../../../@types/slate";
import { useState } from "react";
import { createEditor, Node, Transforms } from "slate";
import {
    ReactEditor,
    RenderElementProps,
    Slate,
    useFocused,
    useSelected,
    withReact
} from "slate-react";

import EditableTextArea from "../../../apps/containers/AppContainer/Slate";
import { MAX_NUMBER_OF_MEMBERS_TO_INVITE } from "../../../apps/containers/SignupCompleteContainer/types";

import { Keyboard } from "../../../foundation/enums";
import Mailcheck from "../../../foundation/mailcheck";
import classname from "../../../foundation/utils/classname";
import { isEmailValid } from "../../../foundation/utils/string";

import styles from "./WorkspaceMembersFormInput.module.css";

interface Props {
    inputValue: Node[];
    fixedHeight?: boolean;
    onChange: (newValue: Node[]) => void;
    onError: (message: string | null) => void;
    onInputValid: () => void;
    isInputValidityPending: boolean;
}

function WorkspaceMembersFormInput({
    inputValue,
    fixedHeight,
    onChange,
    onError,
    onInputValid,
    isInputValidityPending
}: Props) {
    const editor = useMemo(() => withEmbeds(withReact(createEditor())), []);
    const [isComposing, setComposing] = useState(false);

    useEffect(() => {
        if (isInputValidityPending) {
            checkValidity();
        }
    }, [isInputValidityPending]);

    function renderElement(props: RenderElementProps) {
        return <Element {...props}/>;
    }

    function handleKeyDown(event: KeyboardEvent) {
        if (event.key === Keyboard.Key.Enter ||
            event.key === Keyboard.Key.Tab ||
            event.key === "," ||
            event.code === Keyboard.Code.Space
        ) {
            event.preventDefault();

            checkValidity();
        }
    }

    function checkValidity() {
        const selectionIndex = getSelectionIndex();
        const value = inputValue[0].children[selectionIndex].text ?? "";
        if (selectionIndex !== -1 && !value) {
            if (isInputValidityPending) {
                onInputValid();
            }

            return;
        }

        const emailElements = editor.children[0].children as MemberEmailElement[];
        if (emailElements.filter(e => e.type === "emailElement").length === MAX_NUMBER_OF_MEMBERS_TO_INVITE) {
            onError(`Free trials have a <strong>25 seats</strong> limit.`);

            return;
        }

        validateEmail(value, true);
    }

    function getSelectionIndex() {
        return editor.selection?.anchor.path[1] ?? -1;
    }

    function getSelectedEmailElement() {
        const selectionIndex = getSelectionIndex();
        if (selectionIndex !== -1) {
            return editor.children[0].children[selectionIndex];
        }

        return null;
    }

    if (editor.selection) {
        const selectionIndex = getSelectionIndex();
        if (selectionIndex !== -1) {
            const selectedEmailElement = getSelectedEmailElement();
            const selectionText = selectedEmailElement?.text as string;

            // This hack was added for recalculating text length next to a query block to
            // make scroll follow newly typed letters. Otherwise, horizontal scroll does not folloy user input
            if (selectionText !== undefined && selectionText.length === 1) {
                Transforms.deselect(editor);
                Transforms.select(editor, [0, selectionIndex]);
                Transforms.collapse(editor, { edge: "end" });
            }
        }
    }

    function insertEmail(value: string) {
        const emailElements = editor.children[0].children as MemberEmailElement[];

        const emailBlock = [
            {
                type: "emailElement",
                value,
                children: [{ text: "" }]
            }
        ] as MemberEmailElement[];

        const selectionIndex = getSelectionIndex();
        if (selectionIndex !== -1) {
            const selection = emailElements[selectionIndex];

            if (selection.text !== undefined) {
                Transforms.deselect(editor); // prevent autoselect after deleting node
                Transforms.delete(editor, { at: [0, selectionIndex] });
                Transforms.insertNodes(editor, emailBlock, {
                    at: [0, selectionIndex]
                });
                // +2 is added for selecting the blank element which will automatically be added after adding the block
                Transforms.select(editor, [0, selectionIndex + 2]);
            }
        }
    }

    function withEmbeds(slateEditor: ReactEditor) {
        const { isVoid, isInline } = slateEditor;
        slateEditor.isVoid = element => (element.type === "emailElement" ? true : isVoid(element));
        slateEditor.isInline = element => (element.type === "emailElement" ? true : isInline(element));
        return slateEditor;
    }

    function handlePaste(event: ClipboardEvent) {
        if (!event.clipboardData) {
            return;
        }

        const selectionIndex = getSelectionIndex();

        if (event.clipboardData.getData("text/plain") && selectionIndex !== -1) {
            event.preventDefault();

            const copiedText = event.clipboardData!.getData("text/plain");

            let nodes = copiedText.split(/,\s|\s|,|\n/).filter(node => node.length !== 0);

            if (!nodes.length) {
                return;
            }

            const emailElements = editor.children[0].children as MemberEmailElement[];
            const emailElementsCount = emailElements.filter(e => e.type === "emailElement").length;
            const totalElementCount = emailElementsCount + nodes.length;

            if (totalElementCount > MAX_NUMBER_OF_MEMBERS_TO_INVITE) {
                nodes = nodes.slice(
                    0,
                    Math.min(MAX_NUMBER_OF_MEMBERS_TO_INVITE - emailElementsCount, nodes.length)
                );
            }

            if (nodes.length === 1) {
                validateEmail(nodes[0], true);
            } else {
                nodes.forEach(node => validateEmail(node));
            }
        }
    }

    function validateEmail(value: string, displayError = false) {
        if (!isEmailValid(value)) {
            if (displayError && value) {
                onError(`Email address <span>${value}</span> doesn’t look right!`);
            }

            return;
        }

        Mailcheck.run({
            email: value,
            suggested() {
                if (displayError && value) {
                    onError(`Email address <span>${value}</span> doesn’t look right!`);
                }
            },
            empty() {
                insertEmail(value);

                if (isInputValidityPending) {
                    onInputValid();
                }
            }
        });
    }

    function handleValueChange(newValue: Node[]) {
        if (newValue !== inputValue) {
            onError(null);
            onChange(newValue);
        }
    }

    return (
        <div className={classname(styles.workspaceMembersFormInput, { [styles.fixedHeight]: fixedHeight })}>
            <Slate editor={editor} value={inputValue} onChange={handleValueChange}>
                <EditableTextArea
                    renderElement={renderElement}
                    /* @ts-ignore */ // latest types library 0.22.9 does not have placeholder type definition as element
                    placeholder={isComposing ? "" : <span class={styles.placeholder}>Ex. zo@zeplin.io, bran@zeplin.io</span>}
                    /* @ts-ignore */ // library expects React event
                    onKeyDown={handleKeyDown}
                    /* @ts-ignore */
                    onPaste={handlePaste}
                    setComposing={setComposing}/>
            </Slate>
        </div>
    );
}

function Element(props: RenderElementProps) {
    const { attributes, children, element } = props;
    switch (element.type) {
        case "emailElement":
            return <EmailElement {...props}>{children}</EmailElement>;
        default:
            return <div class={styles.container} {...attributes}>{children}</div>;
    }
}

function EmailElement({ attributes, children, element }: RenderElementProps) {
    const value = element.value as string;
    const selected = useSelected();
    const focused = useFocused();

    return (
        <div
            class={classname(styles.emailBlock, { [styles.selected]: focused && selected })}
            {...attributes}
            contentEditable={false}>
            <span>{value}</span>
            {children}
        </div>
    );
}

export default WorkspaceMembersFormInput;
